import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useEditor } from '../contexts/EditorContext';
import { useThread } from '../contexts/ThreadContext';
import { apiGetBrandGuidelines } from 'services/apiService';

const BrandGuidelinesToggle: React.FC<{ category?: "creative" | "layout"; hideAssets?: boolean }> = ({ category, hideAssets }) => {
  const { currentProjectId: projectId } = useThread();
  const { useBrandGuidelines, setUseBrandGuidelines, excludedAssetIds, setExcludedAssetIds, brandAssets, setBrandAssets, brandDefaultApplied } = useSettings();
  const { isLoading, editModel } = useEditor();
  const [expanded, setExpanded] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);

  const CREATIVE_TYPES: BrandAssetType[] = [
    "logo",
    "mannequin",
    "hero_ad",
    "lighting_ref",
    "product_shot_ref",
  ];
  const LAYOUT_TYPES: BrandAssetType[] = ["template"];
  // Derived identity visuals (palette_image, typography_image) are backend-owned —
  // we do not surface them as user-togglable in this list.

  const visibleAssets = category === "layout"
    ? brandAssets.filter((a) => LAYOUT_TYPES.includes(a.type))
    : category === "creative"
    ? brandAssets.filter((a) => CREATIVE_TYPES.includes(a.type))
    : brandAssets.filter((a) => [...CREATIVE_TYPES, ...LAYOUT_TYPES].includes(a.type));

  // Reset brand state when switching projects
  const prevProjectId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (projectId && projectId !== prevProjectId.current) {
      if (prevProjectId.current !== undefined) {
        // Only reset if we're actually switching (not on initial load)
        setBrandAssets([]);
        setUseBrandGuidelines(false);
        setExcludedAssetIds([]);
        brandDefaultApplied.current = false;
      }
      prevProjectId.current = projectId;
    }
  }, [projectId, setBrandAssets, setUseBrandGuidelines, setExcludedAssetIds, brandDefaultApplied]);

  useEffect(() => {
    if (projectId && brandAssets.length === 0) {
      setAssetsLoading(true);
      apiGetBrandGuidelines(projectId)
        .then((bg) => {
          const assets = bg.assets || [];
          setBrandAssets(assets);
          if (!brandDefaultApplied.current && assets.length > 0) {
            brandDefaultApplied.current = true;
            setUseBrandGuidelines(true);
          }
        })
        .catch(() => {})
        .finally(() => setAssetsLoading(false));
    }
  }, [projectId, brandAssets.length, setBrandAssets, setUseBrandGuidelines, brandDefaultApplied]);

  const toggleAsset = (assetId: string) => {
    setExcludedAssetIds((prev) =>
      prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]
    );
  };

  const disabled = isLoading;
  const partialSupport = editModel === 'openai';

  return (
    <div className="w-full">
      <div className="flex items-center justify-between w-full">
        <span className="text-sm font-medium text-gray-300">Brand guidelines</span>
        <label
          htmlFor="brand-guidelines-toggle"
          className={`flex items-center gap-2 cursor-pointer transition-opacity ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={partialSupport ? 'ChatGPT Image 2.5 uses brand text rules (voice, photography style, colour hex, do-not list). Brand images (logos, templates) are sent as text references only.' : ''}
        >
          <div className="relative">
            <input
              id="brand-guidelines-toggle"
              type="checkbox"
              className="sr-only peer"
              checked={useBrandGuidelines}
              disabled={disabled}
              onChange={() => setUseBrandGuidelines(!useBrandGuidelines)}
            />
            <div className="block w-10 h-5 bg-white/20 rounded-full peer-checked:bg-blue-600 transition"></div>
            <div className="dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
          </div>
        </label>
      </div>

      {useBrandGuidelines && !hideAssets && (
        <div className="mt-2">
          {assetsLoading ? (
            <span className="text-xs text-gray-500">Loading assets...</span>
          ) : visibleAssets.length === 0 ? (
            <span className="text-xs text-gray-500">No brand assets configured</span>
          ) : (
            <>
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-300 transition-colors"
              >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {visibleAssets.filter(a => !excludedAssetIds.includes(a.id)).length} of {visibleAssets.length} asset{visibleAssets.length !== 1 ? "s" : ""} included
              </button>
              {expanded && (
                <div className="mt-1 space-y-1 max-h-40 overflow-y-auto">
                  {visibleAssets.map((asset) => (
                    <label key={asset.id} className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer hover:bg-white/5 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={!excludedAssetIds.includes(asset.id)}
                        onChange={() => toggleAsset(asset.id)}
                        className="rounded border-gray-600"
                      />
                      {(asset.thumbnail_url || asset.image_url) && (
                        <img src={asset.thumbnail_url || asset.image_url} alt="" className="w-5 h-5 object-contain rounded" />
                      )}
                      <span className="truncate">{asset.name}</span>
                      <span className="text-gray-500 ml-auto">{asset.type}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BrandGuidelinesToggle;
